import styled from 'styled-components';
import * as colors from 'components/common/colorConstants';

const fonts = {
  medium: `'Roboto', sans-serif`,
  regular: `'Roboto', sans-serif`,
};

export const PickerContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
`;

export const TriggerInput = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 25px;
  padding: 0 6px 0 0;
  background: ${colors.white};
  border: 1px solid ${(props) => (props.$isOpen ? colors.blue1 : colors.lightGray21)};
  border-radius: 4px;
  cursor: pointer;
  width: 100%;

  &:hover {
    border-color: ${(props) => (props.$isOpen ? colors.blue1 : colors.lightGray1)};
  }
`;

export const CalendarIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  background: ${colors.lightGray7};
  border-radius: 2px;
  width: 21px;
  height: 21px;
  flex-shrink: 0;
  color: ${colors.blue1};
`;

export const TriggerText = styled.span`
  font-family: ${fonts.medium};
  font-size: 11px;
  font-weight: 500;
  line-height: 1.45;
  color: ${(props) => (props.$isPlaceholder ? colors.inputGrey : colors.black4)};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PickerWidget = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  width: 328px;
  background: ${colors.white};
  border: 1px solid ${colors.lightGray21};
  border-radius: 8px;
  overflow: visible;
  box-shadow: 0px 10px 16px 0px rgba(10, 19, 30, 0.1);
`;

export const CalendarSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  gap: 4px;
`;

export const MonthHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: ${colors.white};
  padding: 4px 0;
`;

export const MonthNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: ${colors.lightGray14};
  border: none;
  border-radius: 500px;
  padding: 4px;
  cursor: pointer;
  color: ${colors.black4};

  &:hover {
    opacity: 0.8;
  }
`;

export const MonthTitle = styled.p`
  flex: 1;
  font-family: ${fonts.medium};
  font-weight: 500;
  font-size: 11px;
  line-height: 1.45;
  color: ${colors.black4};
  text-align: center;
  margin: 0;
`;

export const DaysOfWeekRow = styled.div`
  display: flex;
  width: 100%;
`;

export const WeekRow = styled.div`
  display: flex;
  width: 100%;
`;

export const DayCell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 34px;
  background: ${colors.white};
  cursor: pointer;
  box-sizing: border-box;
  position: relative;
  font-family: ${fonts.medium};
  font-weight: 500;
  font-size: 11px;
  line-height: 1.45;
  color: ${colors.lightGray2};
`;

export const CellHighlight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 7px;
  box-sizing: border-box;
  position: ${(props) =>
    (props.isStart && !props.isEnd) || (props.isEnd && !props.isStart) || props.isInRange || props.isHoverEnd
      ? 'absolute'
      : 'relative'};
  top: ${(props) =>
    (props.isStart && !props.isEnd) || (props.isEnd && !props.isStart) || props.isInRange || props.isHoverEnd
      ? '0'
      : 'auto'};
  bottom: ${(props) =>
    (props.isStart && !props.isEnd) || (props.isEnd && !props.isStart) || props.isInRange || props.isHoverEnd
      ? '0'
      : 'auto'};
  left: ${(props) => {
    if (props.isStart && !props.isEnd) {
      return '3px';
    }

    if ((props.isEnd && !props.isStart) || props.isInRange || props.isHoverEnd) {
      return '0';
    }

    return 'auto';
  }};
  right: ${(props) => {
    if (props.isEnd && !props.isStart) {
      return '3px';
    }

    if ((props.isStart && !props.isEnd) || props.isInRange || props.isHoverEnd) {
      return '0';
    }

    return 'auto';
  }};
  width: ${(props) =>
    (props.isStart && !props.isEnd) || (props.isEnd && !props.isStart) || props.isInRange || props.isHoverEnd
      ? 'auto'
      : '100%'};
  height: ${(props) =>
    (props.isStart && !props.isEnd) || (props.isEnd && !props.isStart) || props.isInRange || props.isHoverEnd
      ? 'auto'
      : '100%'};
  background: ${(props) => {
    if (props.isStart || props.isEnd || props.isHoverEnd) {
      return colors.purple1;
    }

    if (props.isInRange) {
      return colors.purple2;
    }

    return colors.white;
  }};
  border-radius: ${(props) => {
    if (props.isStart && props.isEnd) {
      return '6px';
    }

    if (props.isStart) {
      return '6px 0 0 6px';
    }

    if (props.isEnd || props.isHoverEnd) {
      return '0 6px 6px 0';
    }

    if (props.isInRange) {
      return '0';
    }

    return '6px';
  }};
  border: ${(props) => (props.isToday && !props.isStart && !props.isEnd ? `1px solid ${colors.lightGray1}` : 'none')};
`;

export const DayText = styled.p`
  font-family: ${fonts.medium};
  font-weight: 500;
  font-size: 11px;
  line-height: 1.45;
  color: ${(props) => {
    if (props.isWeekday || props.isDisabled) {
      return colors.lightGray2;
    }

    if (props.isSelected) {
      return colors.white;
    }

    return colors.black4;
  }};
  text-align: center;
  margin: 0;
  width: 18px;
  height: 18px;
  position: relative;
  z-index: 1;
`;

export const TimeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid ${colors.lightGray21};
  border-bottom: 1px solid ${colors.lightGray21};
`;

export const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TimeLabel = styled.p`
  font-family: ${fonts.medium};
  font-weight: 500;
  font-size: 11px;
  line-height: 1.45;
  color: ${colors.black4};
  margin: 0;
  white-space: nowrap;
  min-width: 80px;
  flex-shrink: 0;
`;

export const TimeInputsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TimeDropdownsContainer = styled.div`
  flex: 1;
  display: flex;
  gap: 6px;
  align-items: center;
`;

export const TimeDropdown = styled.div`
  flex: ${(props) => (props.$isAmPm ? '0 0 auto' : '1')};
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isAmPm ? 'center' : 'space-between')};
  gap: ${(props) => (props.$isAmPm ? '3px' : '3px')};
  background: ${colors.white};
  border: 1px solid ${colors.lightGray21};
  border-radius: 4px;
  padding: ${(props) => (props.$isAmPm ? '5px 6px' : '5px 6px 5px 8px')};
  cursor: ${(props) => (props.$isAmPm ? 'pointer' : 'default')};
  position: relative;
  min-width: ${(props) => (props.$isAmPm ? '50px' : '0')};
  max-width: ${(props) => (props.$isAmPm ? '50px' : 'none')};

  svg {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    cursor: pointer;
    position: relative;
    z-index: 2;
    pointer-events: none;
  }

  select {
    position: absolute;
    top: 0;
    right: 0;
    width: ${(props) => (props.$isAmPm ? '100%' : '30px')};
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: 1;
  }
`;

export const TimeValue = styled.input`
  font-family: ${fonts.medium};
  font-weight: 500;
  font-size: 11px;
  line-height: 1.45;
  color: ${colors.black4};
  margin: 0;
  white-space: nowrap;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0;
  width: ${(props) => (props.$isAmPm ? '22px' : '14px')};
  text-align: left;
  cursor: text;
  outline: none;
  position: relative;
  z-index: 2;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const TimeUnit = styled.p`
  font-family: ${fonts.regular};
  font-weight: 400;
  font-size: 11px;
  line-height: 1.45;
  color: ${colors.inputGrey};
  margin: 0;
  white-space: nowrap;
  flex-shrink: 0;
  pointer-events: none;
`;

export const ActionButtonsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
`;

export const SelectedDateText = styled.div`
  flex: 1;
  font-family: ${fonts.medium};
  font-weight: 500;
  font-size: 11px;
  line-height: 1.45;
  color: ${colors.black4};

  .label {
    color: ${colors.darkGray1};
    font-family: ${fonts.medium};
    font-weight: 500;
    font-size: 11px;
    padding-left: 0;
  }
`;

export const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.white};
  border: 1px solid ${colors.lightGray1};
  border-radius: 4px;
  padding: 5px 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

export const ApplyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.blue1};
  border: none;
  border-radius: 4px;
  padding: 5px 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

export const ButtonText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 4px;
  font-family: ${fonts.medium};
  font-weight: 500;
  font-size: 11px;
  line-height: 1.45;
  color: ${(props) => (props.$isWhite ? colors.white : colors.black4)};
  text-align: center;
  white-space: nowrap;
`;
